// src/auth.tsx
import {
	type SandboxedProviderInfo,
	setSandboxedProviders,
} from "@/shared/sandboxed-providers.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { flushSync } from "react-dom";
import type {
	UiPreferenceKey,
	UiPreferences,
} from "@/shared/ui-preferences.ts";
import { AuthConnectionError } from "./components/auth/auth-connection-error.tsx";
import { MotionLoaderFullscreen } from "./components/custom/motion-loader.tsx";
import { useSessionExpiryMonitor } from "./hooks/use-session-expiry-monitor.ts";
import {
	checkAuthQueryOptions,
	loginUser,
	loginWithPreviewIdentity,
} from "./services/auth-service.ts";
import { isLoginError, LoginError } from "./types/auth-errors.ts";
import type { AuthState, User, UserCapabilities } from "./types/auth.ts";
import {
	didSessionExpireRecently,
	getAccessToken,
	handleSessionExpired,
	registerAuthCallbacks,
	updateAuthHeaders,
} from "./utils/fetch.ts";
import {
	forgetAllAccounts,
	rememberAccount,
} from "./utils/remembered-accounts.ts";
import { nextBackoffDelayMs } from "./utils/backoff.ts";
import { classifyAuthTokenStorageEvent } from "./utils/cross-tab-session.ts";
import {
	type BootFailureKind,
	classifyBootFailure,
	serviceStatusQueryOptions,
} from "./services/status-service.ts";
import {
	getTracer,
	getActiveSpan,
	setTelemetryUser,
	SpanStatusCode,
} from "./utils/telemetry.ts";
import { m } from "./paraglide/messages.js";
import { toast } from "@/utils/toast.ts";
import { clearErrorLog } from "@/hooks/error-log-store.ts";
import { setLocale } from "./paraglide/runtime.js";
import type { BillingRelationship, Plan } from "./shared/plans.ts";
import type { WorkspaceSeatTier } from "./shared/seat-tiers.ts";

const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Hard ceiling on restoring the session at boot.
 *
 * Everything the app renders is gated on that one request, and it had no bound
 * at all: an app node that accepts the connection but never answers — a hung
 * database, a gateway holding the request open — left the fullscreen spinner up
 * forever with no error and no way out. Ten seconds is far above a healthy
 * check (the backend gives up on Postgres after five) and far below a user's
 * patience for a screen that never changes.
 *
 * Scoped deliberately to the bootstrap. A blanket timeout on `$api` would break
 * the long-running calls the product is made of — websearch, RAG, multi-tool
 * chains — which is why the client, not the endpoint, is where a timeout may
 * live, and why this one names exactly the request it bounds.
 */
const AUTH_BOOTSTRAP_TIMEOUT_MS = 10_000;

/** When to admit the wait is unusual, without giving up on it yet. */
const AUTH_BOOTSTRAP_SLOW_MS = 4_000;

/**
 * How recently the 401 flow must have fired for a failed restore to count as a
 * genuine expiry rather than an outage. Comfortably longer than the request it
 * accompanies, short enough that an expiry from an earlier page view cannot be
 * mistaken for this one.
 */
const SESSION_EXPIRY_ATTRIBUTION_MS = 30_000;

/** Stop counting attempts once the delay has reached its ceiling. */
const MAX_TRACKED_RETRY_ATTEMPTS = 6;

/**
 * How long the failure screen may wait for the gateway to say whose fault this
 * is. Short, because it is spent on top of a wait the user has already endured;
 * long enough that a same-origin document normally answers within it.
 */
const CLASSIFY_BUDGET_MS = 1_500;

/**
 * A failed session restore, together with its whole retry schedule.
 *
 * `retryAt` is an absolute timestamp rather than a duration on purpose. The
 * delay is jittered, so recomputing it during render would hand the countdown a
 * different number every time React re-rendered — which silently re-armed the
 * retry timer on each render and stopped the backoff from ever growing. Fixing
 * the moment once, when the failure is recorded, is what makes the schedule
 * mean something.
 */
interface BootFailure {
	kind: BootFailureKind;
	/** 0-based; drives the backoff and stops growing at the cap. */
	attempt: number;
	/** `Date.now()` milliseconds at which the automatic retry fires. */
	retryAt: number;
}

class AuthBootstrapTimeout extends Error {
	constructor() {
		super("Timed out restoring the session");
		this.name = "AuthBootstrapTimeout";
	}
}

/**
 * Race a bootstrap request against the deadline above.
 *
 * The underlying request is intentionally NOT aborted when the deadline wins:
 * it is a plain GET whose result react-query will happily cache, so letting it
 * land keeps a slow-but-successful check useful to the retry that follows.
 */
async function withBootstrapDeadline<T>(work: Promise<T>): Promise<T> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	try {
		return await Promise.race([
			work,
			new Promise<never>((_, reject) => {
				timer = setTimeout(
					() => reject(new AuthBootstrapTimeout()),
					AUTH_BOOTSTRAP_TIMEOUT_MS,
				);
			}),
		]);
	} finally {
		clearTimeout(timer);
	}
}

/**
 * Build user object from API response with consistent structure
 */
function buildUser(userData: {
	userId: string;
	orgId: string;
	orgName: string;
	orgPlan: Plan;
	isAdmin: boolean;
	isOrgFounder: boolean;
	personalSpace: string | null;
	personalProject: string | null;
	email: string;
	isOrgActive: boolean;
	isOrgFrozen: boolean;
	/**
	 * When the organization's contract term begins. Present only while it is frozen
	 * BECAUSE it has not started, which is what distinguishes a customer waiting for
	 * a date from a tenant that was switched off.
	 */
	contractStartsAt?: string;
	contractAwaitingApproval?: boolean;
	contractApproved?: boolean;
	complianceEnabled: boolean;
	copilotEnabled: boolean;
	unoEnabled: boolean;
	unoHintsEnabled: boolean;
	unconfiguredModels: string[];
	modularProviders: SandboxedProviderInfo[];
	modularProvidersAwaitingOrgSetup: string[];
	onboardingCompleted: boolean;
	profile: {
		firstname?: string;
		lastname?: string;
		jobIndustry?: string;
		jobPosition?: string;
	};
	preferredLanguage?: string;
	preferredTheme?: "light" | "dark" | "system";
	suggestionsEnabled: boolean;
	chatMemoryEnabled: boolean;
	uiPreferences: UiPreferences;
	newsletterOptIn: boolean;
	newsletterFeatureEnabled: boolean;
	lastSeenReleaseVersion: string | null;
	latestReleaseVersion: string | null;
	dailyLimitReached: boolean;
	freeUsage: { usedPct: number; resetsAt: string } | null;
	quotaExhausted: boolean;
	subscriptionStatus: string | null;
	isPartnerOrg: boolean;
	billingRelationship: BillingRelationship;
	capabilities: UserCapabilities;
	seatTiersOnSale?: WorkspaceSeatTier[];
}): User {
	// Every session passes through here, so this is where the frontend learns
	// which connector packages the deployment serves: every provider lookup
	// (capabilities, icon, label) falls through to this registry for an id that
	// is not in-tree (`shared/sandboxed-providers.ts`).
	const modularProviders = userData.modularProviders ?? [];
	setSandboxedProviders(modularProviders);
	return {
		id: userData.userId,
		email: userData.email,
		isAdmin: userData.isAdmin,
		isOrgFounder: userData.isOrgFounder,
		personalSpace: userData.personalSpace,
		personalProject: userData.personalProject,
		orgId: userData.orgId,
		orgName: userData.orgName,
		orgPlan: userData.orgPlan,
		isOrgActive: userData.isOrgActive,
		isOrgFrozen: userData.isOrgFrozen,
		contractStartsAt: userData.contractStartsAt,
		contractAwaitingApproval: userData.contractAwaitingApproval,
		contractApproved: userData.contractApproved,
		complianceEnabled: userData.complianceEnabled,
		copilotEnabled: userData.copilotEnabled,
		capabilities: userData.capabilities,
		unoEnabled: userData.unoEnabled,
		unoHintsEnabled: userData.unoHintsEnabled,
		unconfiguredModels: userData.unconfiguredModels,
		modularProviders,
		modularProvidersAwaitingOrgSetup:
			userData.modularProvidersAwaitingOrgSetup ?? [],
		onboardingCompleted: userData.onboardingCompleted,
		profile: userData.profile,
		preferredLanguage: userData.preferredLanguage,
		preferredTheme: userData.preferredTheme,
		suggestionsEnabled: userData.suggestionsEnabled,
		chatMemoryEnabled: userData.chatMemoryEnabled,
		uiPreferences: userData.uiPreferences,
		newsletterOptIn: userData.newsletterOptIn,
		newsletterFeatureEnabled: userData.newsletterFeatureEnabled,
		lastSeenReleaseVersion: userData.lastSeenReleaseVersion,
		latestReleaseVersion: userData.latestReleaseVersion,
		dailyLimitReached: userData.dailyLimitReached,
		freeUsage: userData.freeUsage,
		quotaExhausted: userData.quotaExhausted,
		subscriptionStatus: userData.subscriptionStatus,
		isPartnerOrg: userData.isPartnerOrg,
		billingRelationship: userData.billingRelationship,
		seatTiersOnSale: userData.seatTiersOnSale ?? ["standard"],
	};
}

/** The hard cutoff `/api/auth/check` reports for the session, if it has one. */
function sessionExpiresAtOf(userData: unknown): string | null {
	return (
		(userData as { sessionExpiresAt?: string | null }).sessionExpiresAt ?? null
	);
}

/**
 * Make a validated session available to the organization switcher. Called from
 * both places a session becomes active — `completeLogin` and the boot restore —
 * so a session that predates the switcher is picked up on the next page load.
 */
function rememberSession(
	token: string,
	user: User,
	sessionExpiresAt: string | null,
): void {
	rememberAccount({
		userId: user.id,
		orgId: user.orgId,
		orgName: user.orgName,
		email: user.email,
		token,
		sessionExpiresAt,
	});
}

/**
 * Start the app over on whatever session `authToken` now holds — the one way an
 * organization switch takes effect.
 *
 * A full page load rather than an in-place swap because session state does not
 * live in React alone: the chat, Uno, activity and voice WebSockets, the stream
 * managers, the query cache, telemetry identity and the locale are all bound to
 * the session they started with. Resetting each by hand would hold only until
 * the next singleton is added; a reload holds by construction.
 *
 * `/home` rather than the current path: a chat or space id from the previous
 * organization is a not-found in the next one.
 */
function reloadIntoActiveSession(): void {
	window.location.assign("/home");
}

/** Make `token` the active session and start the app over on it. */
function activateAccount(token: string): void {
	localStorage.setItem("authToken", token);
	reloadIntoActiveSession();
}

/** The message a failed passkey sign-in shows; shared by every passkey entry point. */
function loginErrorMessage(error: unknown): string {
	return isLoginError(error)
		? new LoginError(error.code).message
		: m.auth_general_auth_error();
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [authError, setAuthError] = useState<Error | null>(null);
	const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null);
	// Set when restoring the session on load fails for a non-auth reason (backend
	// unreachable, 5xx, network, or the deadline expiring). This is not a
	// sign-out — the token is kept and the user is shown a retryable error screen
	// instead of being sent to login. `null` means the restore has not failed.
	const [bootFailure, setBootFailure] = useState<BootFailure | null>(null);
	// Consecutive failed restores. A ref, not state, because every retry clears
	// `bootFailure` to put the spinner back up — so the count cannot live inside
	// the failure it is counting, or it would reset to zero on each attempt and
	// the backoff would never grow past its first step.
	const bootAttemptRef = useRef(0);
	// Flips while the bootstrap is still running but has outlived the point where
	// silence stops being reassuring. Only changes the spinner's caption.
	const [isBootstrapSlow, setIsBootstrapSlow] = useState(false);

	const queryClient = useQueryClient();
	const router = useRouter();

	const logout = useCallback(() => {
		queryClient.cancelQueries();
		// flushSync commits auth state before any further work so the router
		// context the navigate() callback sees is already unauthenticated.
		flushSync(() => {
			setUser(null);
			setIsAuthenticated(false);
			setAuthError(null);
			setSessionExpiresAt(null);
			// A boot failure left set here would render the error screen over the
			// login page the navigation below is heading for.
			setBootFailure(null);
		});
		localStorage.removeItem("authToken");
		updateAuthHeaders(null);
		// Signing out signs out of every remembered organization, not just the
		// active one: on a shared machine, a second tenant left one click away
		// after "sign out" would be the surprise.
		forgetAllAccounts();
		// The error log is the signed-in person's; the next one on this tab
		// must not inherit it.
		clearErrorLog();
		// Intentionally do NOT call queryClient.clear() here. Mounted
		// _authenticated components (OrganizationHeader, etc.) using
		// useSuspenseQuery would see an empty cache, refetch without a token,
		// and throw 401 errors that bypass <Suspense> into the route error
		// boundary. The cache is wiped in completeLogin() instead, where the
		// user is on /auth/login and no authenticated queries are mounted.
	}, [queryClient]);

	// Register logout callback for fetch middleware
	useEffect(() => {
		registerAuthCallbacks(logout, (path: string) => {
			// Only navigate if router is available
			if (router) {
				void router.navigate({ to: path });
			}
		});
	}, [router, logout]);

	const resetAuthState = useCallback((error?: Error | null) => {
		flushSync(() => {
			if (error !== undefined) {
				setAuthError(error);
			}
			setUser(null);
			setIsAuthenticated(false);
			setSessionExpiresAt(null);
		});
		localStorage.removeItem("authToken");
		updateAuthHeaders(null);
	}, []);

	const completeLogin = useCallback(
		async (token: string): Promise<User> => {
			updateAuthHeaders(token);
			localStorage.setItem("authToken", token);
			// Wipe any cache from a previous session before fetching the new
			// user. Static-keyed queries (subscription/*, chats, dashboard, …)
			// would otherwise serve the previous user's data until staleTime
			// elapses, since loginUser() bypasses MutationCache.onSuccess.
			queryClient.clear();
			const userData = await queryClient.fetchQuery(checkAuthQueryOptions());
			const user = buildUser(userData);
			const expiresAt = sessionExpiresAtOf(userData);
			rememberSession(token, user, expiresAt);
			// flushSync so the router context the caller's navigate() sees is
			// already authenticated — without it SSO post-login navigation runs
			// against stale context and bounces through /auth/login.
			flushSync(() => {
				setUser(user);
				setIsAuthenticated(true);
				setAuthError(null);
				setSessionExpiresAt(expiresAt);
			});
			if (
				user.preferredLanguage &&
				(user.preferredLanguage === "en" || user.preferredLanguage === "de")
			) {
				setLocale(user.preferredLanguage);
			}
			const span = getActiveSpan();
			if (span !== undefined) {
				span.setAttribute("user.id", user.id);
				span.setAttribute("user.email", user.email);
			}
			return user;
		},
		[queryClient],
	);

	// Cross-tab propagation of the session, in BOTH directions. `storage` only
	// fires in OTHER tabs, so neither branch can loop back on itself.
	//
	// Clearing (`newValue === null`) — a 401, an explicit sign-out, or the
	// proactive expiry flip — mirrors here immediately so this tab updates its
	// UI instead of waiting for its own next request.
	//
	// Signing in (`newValue` set) used to be ignored, which is why a user with
	// two or three tabs open had to sign in in every one of them: one expiry
	// event darkened all of them, and lighting them back up was three separate
	// logins, each minting its own session. Adopting the token here makes one
	// sign-in cover every open tab.
	//
	// Which of the two an event is — and when it is neither — is
	// `classifyAuthTokenStorageEvent`, where the reasoning and the loop guard
	// are written down and tested. The guard matters: `completeLogin` writes the
	// token back, and writing a value a sibling already holds still raises
	// `storage` there, so adopting while authenticated would have two tabs
	// ping-pong forever.
	//
	// Sits BELOW `completeLogin` rather than with the other listeners, for the
	// same reason `restoreSession` does: it closes over that `const`, and a
	// dependency array is evaluated during render, so declaring it earlier
	// would hit the temporal dead zone on first paint.
	useEffect(() => {
		const onStorage = (event: StorageEvent) => {
			const action = classifyAuthTokenStorageEvent({
				key: event.key,
				newValue: event.newValue,
				isAuthenticated,
				currentToken: getAccessToken(),
			});
			if (action === "ignore") {
				return;
			}
			if (action === "sign-out") {
				handleSessionExpired();
				return;
			}
			if (action === "reload") {
				reloadIntoActiveSession();
				return;
			}
			// `adopt` only ever comes with a token; the classifier is what
			// guarantees that, and narrowing here keeps it provable to the checker.
			const adopted = event.newValue;
			if (adopted === null) {
				return;
			}
			void completeLogin(adopted)
				.then(() => {
					// Only the auth pages are somewhere the user cannot stay once
					// signed in. A public surface — a shared canvas, a legal page —
					// is a legitimate place to be signed in, and yanking a visitor
					// off it because another tab signed in would be its own bug.
					if (router && window.location.pathname.startsWith("/auth/")) {
						void router.navigate({ to: "/home" });
					}
				})
				.catch((error) => {
					// The sibling's token did not validate here (it raced an expiry,
					// or the backend is unreachable). Nothing to report: this tab was
					// already signed out and stays that way.
					console.debug("Cross-tab session adoption failed:", error);
				});
		};
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, [isAuthenticated, completeLogin, router]);

	// Validate the stored token and restore the session. Extracted so the
	// connection-error screen can re-run it on "Try again" without a full reload.
	//
	// `completeLogin` and `resetAuthState` are declared ABOVE this rather than
	// below with the rest of the auth actions, because the preview-identity path
	// here calls `completeLogin` and a `const` is in its temporal dead zone until
	// its own declaration runs.
	// Ask the gateway whose fault a failed restore is. The status document is
	// served by oneDeploy on our own origin, NOT by the oneAI backend, so it
	// keeps answering through a database or app-node outage — which is exactly
	// what makes "oneAI is down" distinguishable from "your network is down".
	// A failure to reach even this is itself the answer: classify as unknown.
	const classifyFailure = useCallback(async (): Promise<BootFailureKind> => {
		try {
			const status = await queryClient.fetchQuery({
				...serviceStatusQueryOptions(),
				staleTime: 0,
			});
			return classifyBootFailure({ status, gatewayReachable: true });
		} catch {
			return classifyBootFailure({ status: null, gatewayReachable: false });
		}
	}, [queryClient]);

	/**
	 * Record a failed restore and schedule the next automatic attempt.
	 *
	 * The classification is awaited before the screen appears, but only for as
	 * long as `CLASSIFY_BUDGET_MS`. Showing the screen first and refining the
	 * wording a moment later reads as a glitch — and worse, the vague wording is
	 * the one the user sees on the very first failure, because the retry fires
	 * before the refinement lands. A user who has already waited out the
	 * bootstrap deadline can afford a fraction of a second for an accurate
	 * message; they cannot afford an inaccurate one.
	 */
	const recordBootFailure = useCallback(async () => {
		const kind = await Promise.race([
			classifyFailure(),
			new Promise<BootFailureKind>((resolve) =>
				setTimeout(() => resolve("unknown"), CLASSIFY_BUDGET_MS),
			),
		]);
		const attempt = Math.min(
			bootAttemptRef.current,
			MAX_TRACKED_RETRY_ATTEMPTS,
		);
		bootAttemptRef.current = attempt + 1;
		setBootFailure({
			kind,
			attempt,
			retryAt: Date.now() + nextBackoffDelayMs(attempt),
		});
	}, [classifyFailure]);

	const restoreSession = useCallback(() => {
		// Initialize token from localStorage (before any requests)
		const token = localStorage.getItem("authToken");
		if (!token) {
			// Inside a oneDeploy PR preview there is nothing to restore and nobody
			// to sign in as: the database is per-PR and empty, and the gateway's
			// firewall identity IS the login (see docs/architecture/pr-previews.md).
			// Ask the backend to exchange it for a session before concluding that
			// this visitor is anonymous.
			//
			// This costs one request that 404s on every deployment that is not a
			// preview. It is deliberately NOT gated on the hostname looking like a
			// preview: it only fires when no token is stored — i.e. on the login
			// page, which already makes an unauthenticated request of its own — and
			// a client-side guess at the platform's hostname scheme would fail
			// silently and unhelpfully the day that scheme changes.
			setIsLoading(true);
			// Deadline-wrapped like the token branch below. This request swallows
			// its own failures, but a swallowed failure is not the same as a
			// bounded one: if it HANGS, `isLoading` never clears and an anonymous
			// visitor gets the same dead spinner a signed-in one used to get.
			withBootstrapDeadline(
				loginWithPreviewIdentity().then((previewToken) =>
					previewToken === null ? undefined : completeLogin(previewToken),
				),
			)
				.catch((error) => {
					// Not a sign-out and not a visible error: the visitor is simply
					// anonymous and belongs on the login page.
					console.debug("Preview session bootstrap failed:", error);
				})
				.finally(() => setIsLoading(false));
			return;
		}
		updateAuthHeaders(token);
		setBootFailure(null);
		setIsBootstrapSlow(false);
		setIsLoading(true);
		const slowTimer = setTimeout(
			() => setIsBootstrapSlow(true),
			AUTH_BOOTSTRAP_SLOW_MS,
		);

		withBootstrapDeadline(
			// `retry` and `networkMode` are overridden HERE rather than on
			// `checkAuthQueryOptions`, so the query key is untouched (an unstable
			// key would thrash the cache the login path and the cache subscription
			// both read) and the login flow keeps the global retry policy.
			//
			// `retry: false` because the error screen's backoff is now the retry
			// mechanism; two competing retry layers would stack ~35s of invisible
			// waiting on top of the deadline. `networkMode: "always"` because the
			// default "online" PAUSES the retryer when `navigator.onLine` is
			// false, and a paused retryer never settles the promise at all — a
			// second, independent way this used to spin forever.
			queryClient.ensureQueryData({
				...checkAuthQueryOptions(),
				retry: false,
				networkMode: "always",
			}),
		)
			.then((userData) => {
				const user = buildUser(userData);
				const expiresAt = sessionExpiresAtOf(userData);
				rememberSession(token, user, expiresAt);
				// The backend answered: the next outage starts from a short delay
				// again rather than inheriting this one's grown backoff.
				bootAttemptRef.current = 0;
				setUser(user);
				setIsAuthenticated(true);
				setAuthError(null);
				setSessionExpiresAt(expiresAt);
				// Set locale from user's preferred language
				if (
					user.preferredLanguage &&
					(user.preferredLanguage === "en" || user.preferredLanguage === "de")
				) {
					setLocale(user.preferredLanguage);
				}
			})
			.catch(async (error) => {
				// A rejected /api/auth/check is one of two very different things:
				//   1. A genuine expired/invalid session (real 401). The authMiddleware
				//      has already run the session-expired flow (cleared the token,
				//      toasted, redirected to login) by the time we get here — nothing
				//      to do, and the token is already gone.
				//   2. A transient failure unrelated to auth: backend not yet started,
				//      5xx, timeout, network blip. The token is still valid — we must
				//      NOT clear it or send the user to login. Show a retryable
				//      connection-error screen instead.
				//
				// We ask the function that OWNS the 401 reaction whether it just fired.
				// This used to infer case 1 from the token being gone, but three other
				// things clear the token — an explicit sign-out, the cross-tab storage
				// listener, the proactive expiry flip — so during an outage a coincident
				// clear from any of them made a dead backend look like an expired
				// session and dropped the user on the login page with a misleading
				// toast. Never clear the token here (that was the older bug — it wiped
				// valid sessions and cascaded to other tabs via the storage listener).
				if (didSessionExpireRecently(SESSION_EXPIRY_ATTRIBUTION_MS)) {
					// Case 1. Clear any failure the retry loop had already put on
					// screen: the middleware has navigated to login, and that
					// navigation is invisible while this provider is rendering the
					// error screen instead of the router.
					setBootFailure(null);
				} else {
					// AWAITED, so `isLoading` (cleared in the `.finally` below, which
					// waits on the promise this handler returns) cannot go false while
					// the classification is still in flight. That gap was long enough
					// for the router to mount unauthenticated and redirect to
					// /auth/login before the error screen could take over — rewriting
					// the URL, and dropping the user at a login page they did not need.
					await recordBootFailure();
				}
				console.debug("Auth token validation failed:", error);
			})
			.finally(() => {
				clearTimeout(slowTimer);
				setIsBootstrapSlow(false);
				setIsLoading(false);
			});
	}, [queryClient, completeLogin, recordBootFailure]);

	// Restore auth state on app load.
	useEffect(() => {
		restoreSession();
	}, [restoreSession]);

	/** Manual retry: reset the backoff so the click acts immediately. */
	const retryRestoreNow = useCallback(() => {
		bootAttemptRef.current = 0;
		restoreSession();
	}, [restoreSession]);

	// Keep retrying a failed restore so a recovered backend heals the app on its
	// own. The timer lives here rather than in the error screen because the
	// schedule is this provider's state — the screen is left purely presentational
	// and cannot get the backoff out of step with the attempt count.
	useEffect(() => {
		if (bootFailure === null) {
			return;
		}
		const timer = setTimeout(
			restoreSession,
			Math.max(0, bootFailure.retryAt - Date.now()),
		);
		return () => clearTimeout(timer);
	}, [bootFailure, restoreSession]);

	useEffect(() => {
		if (!isAuthenticated) {
			return;
		}

		const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
			const query = event?.query;
			if (
				query &&
				query.queryKey[0] === "get" &&
				query.queryKey[1] === "/api/auth/check" &&
				(event?.type === "updated" || event?.type === "added")
			) {
				const userData = query.state.data;
				if (userData && query.state.status === "success") {
					const user = buildUser(userData);
					setUser(user);
					setSessionExpiresAt(sessionExpiresAtOf(userData));
					// Set locale from user's preferred language
					if (
						user.preferredLanguage &&
						(user.preferredLanguage === "en" || user.preferredLanguage === "de")
					) {
						setLocale(user.preferredLanguage);
					}
				}
			}
		});

		return unsubscribe;
	}, [queryClient, isAuthenticated]);

	// Keep Faro's session identity in sync so replays are searchable by email.
	useEffect(() => {
		setTelemetryUser(user ? { id: user.id, email: user.email } : null);
	}, [user]);

	const updateLastSeenReleaseVersion = useCallback((version: string) => {
		setUser((prev) =>
			prev ? { ...prev, lastSeenReleaseVersion: version } : prev,
		);
	}, []);

	const updateSuggestionsEnabled = useCallback((enabled: boolean) => {
		setUser((prev) => (prev ? { ...prev, suggestionsEnabled: enabled } : prev));
	}, []);

	const updateChatMemoryEnabled = useCallback((enabled: boolean) => {
		setUser((prev) => (prev ? { ...prev, chatMemoryEnabled: enabled } : prev));
	}, []);

	const updateNewsletterOptIn = useCallback((optIn: boolean) => {
		setUser((prev) => (prev ? { ...prev, newsletterOptIn: optIn } : prev));
	}, []);

	const updateUiPreference = useCallback(
		(key: UiPreferenceKey, value: boolean) => {
			setUser((prev) =>
				prev
					? { ...prev, uiPreferences: { ...prev.uiPreferences, [key]: value } }
					: prev,
			);
		},
		[],
	);

	const login = useCallback(async (): Promise<User> => {
		return getTracer("frontend").startActiveSpan("login", async (span) => {
			try {
				const response = await loginUser();
				return await completeLogin(response.token);
			} catch (error) {
				const errorMessage = loginErrorMessage(error);
				toast.error(errorMessage);

				const authError =
					error instanceof Error ? error : new Error(errorMessage);
				span.recordException(authError);
				span.setStatus({
					code: SpanStatusCode.ERROR,
					message: authError.message,
				});
				resetAuthState(authError);
				throw authError;
			} finally {
				span.end();
			}
		});
	}, [completeLogin, resetAuthState]);

	// Signing in to a further organization from inside the app. The new session
	// is NOT completed in place — that would rebuild the query cache under the
	// mounted app while its sockets still speak for the previous account — but
	// activated, which reloads onto it. The previous session stays remembered.
	const addAccountWithPasskey = useCallback(async (): Promise<void> => {
		try {
			const { token } = await loginUser();
			activateAccount(token);
		} catch (error) {
			toast.error(loginErrorMessage(error));
		}
	}, []);

	const loginWithToken = useCallback(
		async (token: string) => {
			try {
				await completeLogin(token);
			} catch (error) {
				const authError =
					error instanceof Error
						? error
						: new Error("Failed to complete SSO login");
				resetAuthState(authError);
				throw authError;
			}
		},
		[completeLogin, resetAuthState],
	);

	const handleIdleLogout = useCallback(() => {
		logout();
		toast.info(m.auth_session_idle_logout());
		void router.navigate({ to: "/auth/login" });
	}, [logout, router]);

	const handleSessionWarn = useCallback(
		(remainingMs: number) => {
			// Concrete countdown so the timeout is nachvollziehbar (round up, min 1).
			const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));
			toast.warning(m.auth_session_expiring_soon_minutes({ minutes }), {
				duration: 15000,
				action: {
					// There is no silent-refresh token: re-login mints a fresh 24h
					// session and updates sessionExpiresAt, which extends the window
					// and stops further warnings.
					label: m.auth_session_extend_action(),
					onClick: () => {
						const previousUserId = user?.id;
						void login()
							.then((signedIn) => {
								// The browser's passkey picker offers every account on this
								// device. Picking another organization's passkey here
								// completed that session in place; start over on it so
								// nothing from the previous account outlives the switch.
								if (signedIn.id !== previousUserId) {
									reloadIntoActiveSession();
								}
							})
							.catch(() => {
								// login() already toasts on failure; swallow to avoid unhandled rejection.
							});
					},
				},
			});
		},
		[login, user?.id],
	);

	useSessionExpiryMonitor({
		enabled: isAuthenticated,
		sessionExpiresAt,
		onIdleLogout: handleIdleLogout,
		onWarn: handleSessionWarn,
		// Flip the UI to logged-out the instant the session hits its hard cutoff,
		// not on the next request. Same canonical path as the reactive 401 handler
		// (clears token, toasts, redirects) and debounced so the two dedupe.
		onExpired: handleSessionExpired,
	});

	// Show loading state while checking auth. The caption changes once the wait
	// has outlived the point where an unchanging screen stops being informative.
	if (isLoading) {
		return (
			<MotionLoaderFullscreen
				text={
					isBootstrapSlow ? m.auth_bootstrap_slow() : m.auth_restoring_session()
				}
			/>
		);
	}

	// A transient failure while restoring the session (backend unreachable, 5xx,
	// network, deadline) must not drop the user at the login page — the session
	// is still valid. Hold a retryable error screen instead of rendering the
	// router, and keep retrying on the same backoff the activity stream uses so
	// a recovered backend heals the app without the user doing anything.
	if (bootFailure !== null) {
		return (
			<AuthConnectionError
				kind={bootFailure.kind}
				retryAt={bootFailure.retryAt}
				onRetry={retryRestoreNow}
			/>
		);
	}

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				user,
				isLoading,
				authError,
				login,
				loginWithToken,
				logout,
				activateAccount,
				addAccountWithPasskey,
				updateLastSeenReleaseVersion,
				updateSuggestionsEnabled,
				updateChatMemoryEnabled,
				updateNewsletterOptIn,
				updateUiPreference,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
